import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { Confirm, FlexHorizontal, Spacer, useToasts } from '@vestaboard/installables'
import React, { useEffect, useRef, useState } from 'react'

import { Alert } from '@material-ui/lab'
import { Check } from '@material-ui/icons'
import { FormattedDateTime } from '../../formatted-date-time'
import InfiniteScroll from 'react-infinite-scroller'
import { ListTab } from '../components/ListTab'
import { TruncatedId } from '../../truncated-id'
import { useDeleteScheduledNotification } from '../hooks/useDeleteScheduledNotification'
import { useHistory } from 'react-router'
import { useListScheduledNotifications } from '../hooks/useListScheduledNotifications'
import { useRetryFailedNotifications } from '../hooks/useRetryFailedNotifications'

const FIVE_MINUTES = 5 * 60 * 1000
const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000
const MAX_POLL_DURATION = 6 * 60 * 1000

export const ListNotifications = () => {
  const history = useHistory()
  const { addToast } = useToasts()
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [deleteScheduledNotification] = useDeleteScheduledNotification()
  const [deleted, setDeleted] = useState(false)
  const [retryFailedNotifications] = useRetryFailedNotifications()
  const [retryingId, setRetryingId] = useState<string | null>(null)
  const [initialDelivered, setInitialDelivered] = useState<number | null>(null)
  const pollingStartTime = useRef<number | null>(null)

  const input = {
    cursor: null,
    limit: 25,
    deleted
  }

  const { loading, error, data, fetchMore, refetch, startPolling, stopPolling } = useListScheduledNotifications({
    input
  })

  // Get current stats for the notification being retried
  const retryingNotification = retryingId
    ? data?.listScheduledNotifications.scheduledNotifications.find(n => n.id === retryingId)
    : null
  const numberDelivered = retryingNotification?.numberDelivered ?? 0
  const numberAttempted = retryingNotification?.numberAttempted ?? 0

  // Stop polling when retry is complete or times out
  useEffect(() => {
    if (!retryingId || initialDelivered === null) return

    // Check for timeout
    if (pollingStartTime.current && Date.now() - pollingStartTime.current > MAX_POLL_DURATION) {
      stopPolling()
      setRetryingId(null)
      pollingStartTime.current = null
      addToast('Retry is still processing. Check back later for results.', { appearance: 'success' })
      return
    }

    // Check for completion
    if (numberDelivered >= numberAttempted) {
      stopPolling()
      setRetryingId(null)
      pollingStartTime.current = null
      const newlyDelivered = numberDelivered - initialDelivered
      addToast(
        `Retry complete: ${newlyDelivered} newly delivered, ${numberDelivered} total delivered out of ${numberAttempted} attempted`,
        { appearance: 'success' }
      )
    }
  }, [retryingId, initialDelivered, numberDelivered, numberAttempted, stopPolling, addToast])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling()
  }, [stopPolling])

  const handleRetry = async (scheduledId: string, currentDelivered: number) => {
    setRetryingId(scheduledId)
    setInitialDelivered(currentDelivered)

    try {
      const result = await retryFailedNotifications({
        variables: { scheduledId }
      })

      if (result.errors) {
        addToast('There was an error retrying failed notifications', { appearance: 'error' })
        setRetryingId(null)
        return
      }

      const retryData = result.data?.retryFailedNotificationsV2
      if (retryData?.retried === 0) {
        setRetryingId(null)
        addToast(
          `No notifications needed retry. ${retryData.alreadyDelivered} already delivered out of ${retryData.totalIntended} total.`,
          { appearance: 'success' }
        )
        return
      }

      // Start polling the query for updated delivery stats
      pollingStartTime.current = Date.now()
      startPolling(15000)
    } catch {
      addToast('There was an error retrying failed notifications', { appearance: 'error' })
      setRetryingId(null)
    }
  }

  return (
    <Box>
      <FlexHorizontal spaceBetween>
        <Typography variant='h4'>Notifications</Typography>
        <FlexHorizontal>
          <Button
            onClick={() => {
              setDeleted(deleted => !deleted)
            }}
            variant='contained'
            color='default'>
            {deleted ? 'View Active' : 'View Archived'}
          </Button>
          <div style={{ width: 14 }} />
          <Button onClick={() => history.push('/notifications/create')} variant='contained' color='primary'>
            Send Notification
          </Button>
        </FlexHorizontal>
      </FlexHorizontal>
      <Spacer size='extraLarge' />
      <ListTab activeTab='scheduled' />
      {loading && !data?.listScheduledNotifications.scheduledNotifications ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity='error'>There was an error loading the notifications</Alert>
      ) : !data?.listScheduledNotifications.scheduledNotifications.length ? (
        <Alert severity='info'>There are no notifications</Alert>
      ) : (
        <>
          {data ? (
            <InfiniteScroll
              pageStart={0}
              loadMore={async () => {
                if (isLoadingMore) {
                  return
                }

                setIsLoadingMore(true)
                return await fetchMore({
                  updateQuery(previousQueryResult, options) {
                    const newSearch = options.fetchMoreResult?.listScheduledNotifications.scheduledNotifications || []
                    const previousSearch = previousQueryResult.listScheduledNotifications.scheduledNotifications || []

                    return {
                      listScheduledNotifications: {
                        ...previousQueryResult.listScheduledNotifications,
                        nextCursor: options.fetchMoreResult?.listScheduledNotifications.nextCursor || null,
                        scheduledNotifications: [...previousSearch, ...newSearch]
                      }
                    }
                  },
                  variables: {
                    input: {
                      ...input,
                      cursor: data.listScheduledNotifications.nextCursor || null
                    }
                  }
                }).then(res => {
                  setIsLoadingMore(false)
                  return res
                })
              }}
              hasMore={!!data?.listScheduledNotifications?.nextCursor}
              loader={
                <Box>
                  <Spacer size='large' />
                  <CircularProgress />
                </Box>
              }>
              <TableContainer
                component={Paper}
                style={{
                  maxWidth: 'calc(100vw - 290px)',
                  overflowX: 'auto'
                }}>
                <Table
                  style={{
                    width: 1400
                  }}>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: 200 }}>ID</TableCell>
                      <TableCell>Sent</TableCell>
                      <TableCell style={{ width: 250 }}>Send Time</TableCell>
                      <TableCell style={{ width: 350 }}>Title</TableCell>
                      <TableCell>Tag</TableCell>
                      <TableCell>Attempted</TableCell>
                      <TableCell>Delivered</TableCell>
                      <TableCell>Opened</TableCell>
                      <TableCell>Clicks</TableCell>
                      <TableCell style={{ width: 100 }}></TableCell>
                      <TableCell style={{ width: 130 }}></TableCell>
                      <TableCell style={{ width: 100 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.listScheduledNotifications.scheduledNotifications.map(scheduled => {
                      const isSent = !scheduled.sendAt || scheduled.sendAt < Date.now()
                      return (
                        <TableRow key={scheduled.id}>
                          <TableCell>
                            <Button
                              onClick={() => history.push(`/notifications/${scheduled.id}`)}
                              variant='outlined'
                              color='primary'>
                              <TruncatedId value={scheduled.id} />
                            </Button>
                          </TableCell>
                          <TableCell>{isSent ? <Check /> : null}</TableCell>
                          <TableCell>
                            <FormattedDateTime value={scheduled.sendAt || scheduled.createdAt} />
                          </TableCell>
                          <TableCell>{scheduled.title}</TableCell>
                          <TableCell>{scheduled.tag?.title}</TableCell>
                          <TableCell>{scheduled.numberAttempted || 0}</TableCell>
                          <TableCell>{scheduled.numberDelivered || 0}</TableCell>
                          <TableCell>{scheduled.numberOpened}</TableCell>
                          <TableCell>{scheduled.numberClicked || 0}</TableCell>
                          <TableCell>
                            {isSent ? (
                              <Button
                                onClick={() => history.push(`/notifications/${scheduled.id}/delivered`)}
                                variant='outlined'
                                color='primary'>
                                Delivered
                              </Button>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const sentAt = scheduled.sendAt || scheduled.createdAt
                              const timeSinceSent = Date.now() - sentAt
                              const canRetry = isSent && timeSinceSent > FIVE_MINUTES && timeSinceSent < TWENTY_FOUR_HOURS
                              const isRetrying = retryingId === scheduled.id
                              if (!canRetry) return null
                              return (
                                <Button
                                  onClick={() => handleRetry(scheduled.id, scheduled.numberDelivered || 0)}
                                  variant='outlined'
                                  color='secondary'
                                  disabled={!!retryingId}>
                                  {isRetrying ? (
                                    <>
                                      <CircularProgress size={16} style={{ marginRight: 4 }} />
                                      Retrying
                                    </>
                                  ) : (
                                    'Retry Undelivered'
                                  )}
                                </Button>
                              )
                            })()}
                          </TableCell>
                          <TableCell>
                            {!deleted ? (
                              <Button
                                onClick={() => setConfirmDelete(scheduled.id)}
                                variant='outlined'
                                color='secondary'>
                                {isSent ? 'Archive' : 'Delete'}
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </InfiniteScroll>
          ) : null}
        </>
      )}
      <Confirm
        open={!!confirmDelete}
        title='Delete Scheduled Notification'
        handleAccept={async () => {
          await deleteScheduledNotification({
            variables: {
              input: {
                id: confirmDelete as string
              }
            }
          }).then(() => {
            setConfirmDelete(null)
            return refetch()
          })
        }}
        handleClose={() => setConfirmDelete(null)}
      />
    </Box>
  )
}
